"use client";

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { ArrowRight, Expand, MousePointer2, Pause, Play } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { phCapture } from "@/utils/analytics";
import { getSportsbookLogoPath } from "@/lib/sportsbook-logo";
import { HERO_TOOL_DEMOS, HERO_TOOL_TIMELINES, TOOL_WORD_MS } from "./hero-tool-demos";
import { getToolCamera } from "./hero-tool-camera";
import styles from "./hero-preview.module.css";

type Playhead = { index: number; elapsed: number; start: number };

function readTour({ index, elapsed, start }: Playhead, viewportRatio: number, reduced: boolean) {
  const preview = HERO_TOOL_DEMOS[index];
  const timeline = HERO_TOOL_TIMELINES[index];
  const stepIndex = Math.max(0, timeline.steps.findLastIndex((step) => elapsed >= step.start));
  const step = timeline.steps[stepIndex];
  const wordIndex = Math.min(step.words.length - 1, Math.floor((elapsed - step.start) / TOOL_WORD_MS));
  const scene = getToolCamera(index, elapsed, preview.width / preview.height, viewportRatio, reduced, start);
  const { pose, spotlight, pointer } = scene;
  return {
    preview, timeline, stepIndex, step, wordIndex, scene,
    camera: { transform: `translate(${pose.x}%, ${pose.y}%) scale(${pose.scale})` },
    focus: spotlight && { transform: `translate(${spotlight.x}cqw, ${spotlight.y}cqh)`, width: `${spotlight.width}%`, height: `${spotlight.height}%`, opacity: scene.opacity },
    pointer: pointer && { transform: `translate(${pointer.x}cqw, ${pointer.y}cqh)`, opacity: scene.opacity },
    pulse: { transform: `translate(-50%, -50%) scale(${1 + scene.pulse * 1.6})`, opacity: 1 - scene.pulse },
    bars: timeline.steps.map((_, bar) => ({ transform: `scaleX(${bar < stepIndex ? 1 : bar === stepIndex ? (elapsed - step.start) / step.duration : 0})` })),
  };
}

const paint = (node: HTMLElement | null | undefined, style: CSSProperties | undefined) => { if (node && style) Object.assign(node.style, style); };

export function HeroPreview() {
  const [playhead, setPlayhead] = useState<Playhead>({ index: 0, elapsed: 0, start: 0 });
  const [hovered, setHovered] = useState(false);
  const [paused, setPaused] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [reduced, setReduced] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const [viewportRatio, setViewportRatio] = useState(2.5);
  const live = useRef(playhead);
  const touchInteraction = useRef(false);
  const draw = useRef(() => {});
  const boardRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const cameraRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLSpanElement>(null);
  const pointerRef = useRef<HTMLSpanElement>(null);
  const pulseRef = useRef<HTMLSpanElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const running = visible && pageVisible && !hovered && !paused && !focused && !reduced && !imageOpen;
  const tour = readTour(playhead, viewportRatio, reduced);
  const { preview, timeline, stepIndex, step, wordIndex, scene } = tour;
  const seek = (next: Playhead) => { live.current = next; setPlayhead(next); };

  useLayoutEffect(() => {
    draw.current = () => {
      const frame = readTour(live.current, viewportRatio, reduced);
      if (frame.preview !== preview) { setPlayhead(live.current); return; }
      paint(cameraRef.current, frame.camera);
      paint(focusRef.current, frame.focus);
      paint(pointerRef.current, frame.pointer);
      paint(pulseRef.current, frame.pulse);
      const bars = stepsRef.current?.querySelectorAll("span");
      frame.bars.forEach((bar, index) => paint(bars?.[index], bar));
      if (frame.scene.cue !== scene.cue || frame.stepIndex !== stepIndex || frame.wordIndex !== wordIndex) setPlayhead(live.current);
    };
    draw.current();
  });

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => setReduced(preference.matches);
    const updateVisibility = () => setPageVisible(!document.hidden);
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0 });
    const resize = new ResizeObserver(([entry]) => { if (entry.contentRect.height > 0) setViewportRatio(entry.contentRect.width / entry.contentRect.height); });
    if (boardRef.current) observer.observe(boardRef.current);
    if (viewportRef.current) resize.observe(viewportRef.current);
    updateMotion();
    updateVisibility();
    preference.addEventListener("change", updateMotion);
    document.addEventListener("visibilitychange", updateVisibility);
    return () => {
      observer.disconnect();
      resize.disconnect();
      preference.removeEventListener("change", updateMotion);
      document.removeEventListener("visibilitychange", updateVisibility);
    };
  }, []);

  useEffect(() => {
    if (!running) return;
    let last = performance.now();
    let timer = 0;
    const tick = (now: number) => {
      const { index, elapsed, start } = live.current;
      const next = elapsed + Math.max(0, now - last);
      last = now;
      live.current = next >= HERO_TOOL_TIMELINES[index].duration ? { index: (index + 1) % HERO_TOOL_DEMOS.length, elapsed: 0, start: 0 } : { index, elapsed: next, start };
      draw.current();
      timer = window.requestAnimationFrame(tick);
    };
    timer = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(timer);
  }, [running]);

  const canPauseForInteraction = () => !touchInteraction.current && !window.matchMedia("(hover: none), (pointer: coarse)").matches;
  const stopForInteraction = (target: EventTarget) => {
    if (canPauseForInteraction() && target instanceof Element && !target.closest("[data-playback-control]")) setPaused(true);
  };
  const updateHover = (target: EventTarget, pointerType: string) => {
    if (pointerType === "touch" || pointerType === "pen") { setHovered(false); return; }
    if (canPauseForInteraction() && target instanceof Element) setHovered(!target.closest("[data-playback-control]"));
  };

  return <div ref={boardRef} className={`${styles.board} hero-stagger-4`} role="region" aria-label="Guided toolkit demo"
    onPointerEnter={(event) => updateHover(event.target, event.pointerType)} onPointerMove={(event) => updateHover(event.target, event.pointerType)} onPointerLeave={() => setHovered(false)}
    onPointerDownCapture={(event) => { touchInteraction.current = event.pointerType === "touch" || event.pointerType === "pen"; if (touchInteraction.current) { setHovered(false); setFocused(false); } stopForInteraction(event.target); }} onClickCapture={(event) => stopForInteraction(event.target)}
    onFocusCapture={(event) => { if (canPauseForInteraction() && event.target.matches(":focus-visible")) setFocused(true); }}
    onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
    <div className={styles.heading}><span>Real screenshots · Saved examples</span></div>
    <div className={styles.selectors} role="group" aria-label="Choose a tool preview">
      {HERO_TOOL_DEMOS.map((item, index) => <button key={item.id} type="button" data-playback-control aria-pressed={playhead.index === index} onClick={() => { seek({ index, elapsed: 0, start: 0 }); setPaused(false); setHovered(false); setFocused(false); phCapture("homepage_tool_preview_selected", { surface: "home_hero", tool: item.id }); }}>{item.name}</button>)}
    </div>
    <div className={styles.opportunity}>
      <h3>{preview.play}</h3><span>{preview.event}</span>
      <div className={styles.numbers}>{preview.metrics.map((metric) => <div key={metric.label}><small>{metric.label}</small><b>{metric.book && <img src={getSportsbookLogoPath(metric.book)} width={22} height={22} alt={metric.bookName} />}<strong className="data-num">{metric.value}</strong></b></div>)}</div>
    </div>
    <Dialog open={imageOpen} onOpenChange={setImageOpen}>
      <div ref={viewportRef} className={styles.viewport}>
        <div ref={cameraRef} className={styles.camera} data-testid="tool-camera" data-cue={scene.cue.at} style={{ aspectRatio: `${preview.width} / ${preview.height}`, ...tour.camera }}>
          <img src={preview.image} alt={preview.alt} width={preview.width} height={preview.height} />
        </div>
        {tour.focus && <span ref={focusRef} className={styles.focus} data-testid="tool-spotlight" aria-hidden="true" style={tour.focus} />}
        {tour.pointer && <span ref={pointerRef} className={styles.pointer} data-testid="tool-pointer" aria-hidden="true" style={tour.pointer}><span ref={pulseRef} style={tour.pulse} /><MousePointer2 size={23} /></span>}
        <span className={styles.focusLabel} aria-hidden="true"><span />{scene.cue.label}</span>
        <DialogTrigger asChild><button type="button" className={styles.expand} aria-label={`Enlarge ${preview.name} screenshot`}><Expand size={13} /> Full screenshot</button></DialogTrigger>
      </div>
      <DialogContent className="w-[calc(100%-24px)] max-w-[1400px]" contentClassName="max-h-[90dvh] overflow-auto p-4 sm:p-6">
        <DialogTitle>{preview.name}</DialogTitle><DialogDescription>Actual Upside interface. This saved example is not a live offer.</DialogDescription>
        <a href={preview.image} target="_blank" rel="noopener noreferrer" className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"><img src={preview.image} alt={preview.alt} width={preview.width} height={preview.height} className="h-auto w-full rounded-lg" /><span className="mt-3 block text-sm text-primary">Open full-resolution screenshot ↗</span></a>
      </DialogContent>
    </Dialog>
    <div className={styles.reading}>
      <h2>{step.takeaway ? "The example bet + why" : preview.title}</h2>
      {step.takeaway && <div className={styles.takeaway} role="group" aria-label="Example bet recap">
        <span>SAVED EXAMPLE · NOT LIVE ODDS</span>
        {step.takeaway.map((bet) => <div key={bet.selection}><img src={getSportsbookLogoPath(bet.book)} width={24} height={24} alt={bet.bookName} /><div><strong>{bet.selection}</strong><small>{bet.details}</small></div></div>)}
      </div>}
      <p className={styles.narration}><span className="sr-only">{step.text}</span><span aria-hidden="true">{step.words.map((word, index) => <span key={`${stepIndex}-${index}`} data-current={!reduced && index === wordIndex} data-read={index < wordIndex}>{word} </span>)}</span></p>
      <div ref={stepsRef} className={styles.steps} role="group" aria-label="Explanation steps">{timeline.steps.map((item, index) => <button type="button" key={item.label} aria-label={`Step ${index + 1}: ${item.label}`} aria-pressed={stepIndex === index} onClick={() => { setPaused(true); seek({ index: playhead.index, elapsed: item.start, start: item.start }); }}><span style={tour.bars[index]} /></button>)}</div>
      <div className={styles.controls}><a href="#how-it-works">Try a price comparison <ArrowRight size={14} /></a>{!reduced && <button type="button" data-playback-control onClick={() => { setPaused(!paused); setFocused(false); }}>{paused ? <Play size={14} /> : <Pause size={14} />}{paused ? "Resume tour" : "Pause tour"}</button>}</div>
    </div>
  </div>;
}
